import { Controller } from "@hotwired/stimulus"

const STREAM_CONTENT_TYPE = "text/vnd.turbo-stream.html"

// Warns before leaving a form with unsaved changes. Turbo navigation (a link
// click, turbo:before-visit) is intercepted and confirmed through an in-app
// styled dialog (the app never uses the browser's native "localhost says…"
// confirm). A hard tab close/refresh (beforeunload) can only use the browser's
// own prompt — that one is not stylable — so it stays native as a last resort.
// A submit releases the guard so the post-save redirect is not blocked.
export default class extends Controller {
  // `save` is the optional Save-button wrapper: it is only shown once the form
  // is dirty (an unsaved edit), so a pristine detail page has no stray Save CTA.
  // `activate` is the optional Activate button: the inverse of save — enabled
  // only while the form is clean, disabled the moment there is an unsaved edit.
  // `sampleAction` marks the sample upload/delete controls: like activate they
  // only work while the screen is clean, with a "save first"
  // hint while disabled. They live inside the lazily-loaded preview frame, so
  // the connected callback applies the current state when they appear.
  // `cancelDialog` is the cancel confirmation: discarding reloads
  // the screen at its last saved state.
  static targets = ["dialog", "save", "activate", "sampleAction", "cancelDialog", "message", "leaveLabel"]
  static values = {
    dirty: Boolean,
    sampleHint: String,
    message: String,
    leaveLabel: String,
    writeMessage: String,
    writeLeaveLabel: String,
  }

  connect() {
    // Seed dirty from the server: a 422 re-render (validation error) reconnects
    // the controller on fresh markup that still holds the operator's unsaved
    // edits, so it must start guarded rather than clean.
    this.dirty = this.dirtyValue
    this.submitting = false
    this.pendingUrl = null
    this.pendingForm = null
    this.pendingSubmitter = null
    this.onBeforeUnload = this.beforeUnload.bind(this)
    this.onBeforeVisit = this.beforeVisit.bind(this)
    this.onConfirmDeclined = this.rearm.bind(this)
    window.addEventListener("beforeunload", this.onBeforeUnload)
    document.addEventListener("turbo:before-visit", this.onBeforeVisit)
    document.addEventListener("cup:confirm-declined", this.onConfirmDeclined)
    this.reflectSave()
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.onBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit)
    document.removeEventListener("cup:confirm-declined", this.onConfirmDeclined)
  }

  markDirty() {
    this.dirty = true
    this.reflectSave()
  }

  reflectSave() {
    if (this.hasSaveTarget) this.saveTarget.classList.toggle("hidden", !this.dirty)
    if (this.hasActivateTarget) this.activateTarget.disabled = this.dirty
    this.sampleActionTargets.forEach((el) => this.reflectSampleAction(el))
  }

  sampleActionTargetConnected(el) {
    this.reflectSampleAction(el)
  }

  reflectSampleAction(el) {
    const disabled = this.dirty
    if (el.tagName === "A") {
      el.classList.toggle("pointer-events-none", disabled)
      if (disabled) el.setAttribute("aria-disabled", "true")
      else el.removeAttribute("aria-disabled")
    } else {
      el.disabled = disabled
    }
    el.classList.toggle("opacity-50", disabled)
    if (disabled) {
      if (el.dataset.cleanTitle === undefined) el.dataset.cleanTitle = el.getAttribute("title") || ""
      if (this.hasSampleHintValue) el.setAttribute("title", this.sampleHintValue)
    } else if (el.dataset.cleanTitle !== undefined) {
      el.setAttribute("title", el.dataset.cleanTitle)
    }
  }

  allowSubmit() {
    this.submitting = true
  }

  // The counterpart of allowSubmit, for a form inside the guarded screen that
  // writes something other than the pending edit. Asking on turbo:before-visit
  // is too late for those: the POST has already been answered, so staying puts
  // the operator back on a stale screen over a write that already committed.
  // Holding the submit itself keeps the choice ahead of the write.
  guardSubmit(event) {
    if (!this.dirty || this.submitting) return
    // Without a dialog there is nothing to answer, so holding the submit would
    // only make the button inert. Let it through instead: a screen that guards
    // without rendering a dialog is a wiring bug, not a reason to swallow
    // clicks.
    if (!this.hasDialogTarget) return
    event.preventDefault()
    this.pendingUrl = null
    this.pendingForm = event.target
    this.pendingSubmitter = event.submitter || null
    this.showDialog(this.writeMessageValue, this.writeLeaveLabelValue)
  }

  // The dialog asks two different questions. Leaving is "your edits are lost
  // because you go elsewhere"; a write inside the screen is "your edits are
  // lost because this replaces the screen" — and after a Turbo Stream answer
  // the operator does not go anywhere at all.
  showDialog(message, leaveLabel) {
    if (this.hasMessageTarget && message) this.messageTarget.textContent = message
    if (this.hasLeaveLabelTarget && leaveLabel) this.leaveLabelTarget.textContent = leaveLabel
    this.dialogTarget.hidden = false
  }

  // Re-arm after a submit that turned out not to navigate. A Turbo Stream
  // answer replaces one panel and leaves the operator on this screen with the
  // same unsaved edit, so a guard still latched open would wave the next link
  // straight through. A missing response means the request never completed —
  // same conclusion. (Do not test fetchResponse.isHTML here: Turbo's own
  // predicate says true for text/vnd.turbo-stream.html.)
  rearmAfterInPlaceSubmit(form) {
    form.addEventListener(
      "turbo:submit-end",
      (event) => {
        const contentType = event.detail?.fetchResponse?.contentType
        if (!contentType || contentType.startsWith(STREAM_CONTENT_TYPE)) this.rearm()
      },
      { once: true }
    )
  }

  rearm() {
    this.submitting = false
  }

  beforeUnload(event) {
    if (!this.dirty || this.submitting) return
    event.preventDefault()
    event.returnValue = ""
  }

  beforeVisit(event) {
    if (!this.dirty || this.submitting) return
    event.preventDefault()
    this.pendingUrl = event.detail?.url || null
    if (this.hasDialogTarget) this.showDialog(this.messageValue, this.leaveLabelValue)
  }

  stay() {
    if (this.hasDialogTarget) this.dialogTarget.hidden = true
    if (this.hasMessageTarget && this.hasMessageValue) this.messageTarget.textContent = this.messageValue
    if (this.hasLeaveLabelTarget && this.hasLeaveLabelValue) this.leaveLabelTarget.textContent = this.leaveLabelValue
    this.pendingUrl = null
    this.pendingForm = null
    this.pendingSubmitter = null
  }

  leave() {
    this.submitting = true
    if (this.hasDialogTarget) this.dialogTarget.hidden = true
    const url = this.pendingUrl
    const form = this.pendingForm
    const submitter = this.pendingSubmitter
    this.pendingForm = null
    this.pendingSubmitter = null
    if (form) {
      this.rearmAfterInPlaceSubmit(form)
      form.requestSubmit(submitter)
    } else if (url && window.Turbo) window.Turbo.visit(url)
    else if (url) window.location.assign(url)
  }

  cancel() {
    if (this.hasCancelDialogTarget) this.cancelDialogTarget.hidden = false
  }

  stayEditing() {
    if (this.hasCancelDialogTarget) this.cancelDialogTarget.hidden = true
  }

  // Discard everything pending: release the guard and revisit the current URL,
  // which re-renders the screen at its last saved state (section and all).
  discard() {
    this.submitting = true
    if (this.hasCancelDialogTarget) this.cancelDialogTarget.hidden = true
    if (window.Turbo) window.Turbo.visit(window.location.href, { action: "replace" })
    else window.location.reload()
  }
}
